import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { DialogLike, DialogService } from "@obg/mat.ui.core";
import { CoreState } from "@obg/ngx.core";
import { TranslationSelector } from "@obg/ngx.core.content";
import { DeviceConfigService } from "@obg/ngx.global";
import { Observable, Subscription } from "rxjs";
import { map, take } from "rxjs/operators";

import { MIGRATION_NOTIFICATION_DIALOG_ID } from "../login-flow-dialog/login-flow-dialog.const";

import { MigrationNotificationContainer } from "./migration-notification-container";

@Injectable({
	providedIn: "root",
})
export class MigrationNotificationDialogService implements OnDestroy {
	private lastShownKey$ = this.store.pipe(
		this.translationSelector.getByKey$("login.migration_notification_last_shown.key"),
		take(1)
	);
	private dialogSubscription$$ = new Subscription();

	constructor(
		private store: Store<CoreState>,
		private translationSelector: TranslationSelector,
		private readonly dialogService: DialogService,
		private readonly deviceConfigService: DeviceConfigService
	) {}

	MIGRATION_NOTIFICATION_DIALOG: DialogLike<MigrationNotificationContainer> = {
		dialogId: MIGRATION_NOTIFICATION_DIALOG_ID,
		dialogComponentType: MigrationNotificationContainer,
		onClose$: (c) => c.close,
	};

	ngOnDestroy(): void {
		this.dialogSubscription$$.unsubscribe();
	}

	open() {
		const shouldShowSubscription = this.shouldShowNotification().subscribe((shouldShow) => {
			if (shouldShow) {
				const dialogRef = this.dialogService.openComponent(this.MIGRATION_NOTIFICATION_DIALOG, {
					disableClose: true,
					panelClass: "obg-m-migration-notification-dialog",
					data: {
						hideClose: true,
						hideFooter: true,
						navbar: false,
						navbarOptions: {
							hideBackButton: true,
						},
					},
					width: this.deviceConfigService.isDesktop() ? "initial" : "100vw",
					height: this.deviceConfigService.isDesktop() ? "initial" : "100vh",
					autoFocus: false,
				});

				this.dialogSubscription$$.add(
					dialogRef.afterClosed().subscribe(() => {
						this.setLastShownDate();
					})
				);

				return dialogRef;
			}
			return null;
		});

		this.dialogSubscription$$.add(shouldShowSubscription);
	}

	private shouldShowNotification(): Observable<boolean> {
		return this.getLastShownDate().pipe(map((lastShownDate) => !lastShownDate));
	}

	private getLastShownDate(): Observable<string | null> {
		return this.getStorageKey().pipe(map((key) => localStorage.getItem(key)));
	}

	private setLastShownDate(): void {
		const setLastShownDateSubscription = this.getStorageKey().subscribe((key) => {
			localStorage.setItem(key, new Date().toISOString());
		});
		this.dialogSubscription$$.add(setLastShownDateSubscription);
	}

	private getStorageKey(): Observable<string> {
		const environment = this.deviceConfigService.isDesktop() ? "desktop" : "mobile";
		return this.lastShownKey$.pipe(map((keyPrefix) => `${keyPrefix}_${environment}`));
	}
}
